import { useState, useEffect } from 'react';
import { GitBranch, Brain, Merge, Filter, Eye, FileDown, Bot, Loader, Search, CheckCircle, AlertCircle } from 'lucide-react';
import {
  semanticAnalysis,
  getInterReferentialGraph,
  filterCompetences,
  exportCorrespondences,
  aiAssistant
} from '../services/matching';

export default function Navette() {
  const [activeTab, setActiveTab] = useState('analyse-semantique');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // États pour l'analyse sémantique
  const [searchText, setSearchText] = useState('');
  const [threshold, setThreshold] = useState(0.7);

  const tabs = [
    { id: 'analyse-semantique', label: 'Analyse Sémantique', icon: Brain },
    { id: 'graphe', label: 'Graphe Inter-référentiels', icon: GitBranch },
    { id: 'filtres', label: 'Filtres Multicritères', icon: Filter },
    { id: 'visualisation', label: 'Visualisation', icon: Eye },
    { id: 'export', label: 'Export', icon: FileDown },
    { id: 'ia-assistant', label: 'Assistant IA', icon: Bot }
  ];

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      switch (activeTab) {
        case 'graphe':
          const graphData = await getInterReferentialGraph();
          setData({ graph: graphData });
          break;
        case 'ia-assistant':
          // Mock data pour IA assistant
          setData({
            suggestions: [
              { competence: 'JavaScript', confidence: 0.95, equivalents: ['ECMAScript', 'JS'] },
              { competence: 'React', confidence: 0.88, equivalents: ['ReactJS', 'React.js'] }
            ]
          });
          break;
        default:
          setData(null);
      }
    } catch (err) {
      console.error('Erreur chargement données:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSemanticAnalysis = async (e) => {
    e.preventDefault();
    if (!searchText) {
      setError('Veuillez entrer un texte à analyser');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await semanticAnalysis('demo-cert-id', null, threshold);
      setData({ analysis: result });
    } catch (err) {
      setError('Erreur lors de l\'analyse sémantique');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setLoading(true);
    setError(null);
    try {
      await exportCorrespondences({ format });
      alert(`Export ${format.toUpperCase()} lancé avec succès !`);
    } catch (err) {
      setError(`Erreur lors de l'export ${format.toUpperCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      );
    }

    switch (activeTab) {
      case 'analyse-semantique':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                Analyse sémantique NLP
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Détecte les compétences identiques ou similaires grâce au traitement du langage naturel
              </p>

              <form onSubmit={handleSemanticAnalysis} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Texte à analyser
                  </label>
                  <textarea
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows="4"
                    placeholder="Entrez une description de compétence..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seuil de similarité: {(threshold * 100).toFixed(0)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={threshold}
                    onChange={(e) => setThreshold(parseFloat(e.target.value))}
                    className="w-full"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white px-6 py-3 rounded-lg font-medium hover:from-purple-600 hover:to-pink-700 transition-all flex items-center justify-center gap-2"
                >
                  <Search className="w-5 h-5" />
                  Analyser
                </button>
              </form>

              {data?.analysis && (
                <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Résultats de l'analyse</h4>
                  <p className="text-sm text-gray-600">
                    {data.analysis.matches?.length || 0} correspondances trouvées
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 'graphe':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-purple-600" />
                Graphe des relations inter-référentiels
              </h3>
              <div className="h-96 bg-gray-50 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                <div className="text-center">
                  <GitBranch className="w-16 h-16 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Visualisation du graphe</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Réseau de compétences connectées entre référentiels
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'filtres':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Filter className="w-5 h-5 text-blue-600" />
                Filtres multicritères
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Secteur</label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="">Tous les secteurs</option>
                    <option value="informatique">Informatique</option>
                    <option value="commerce">Commerce</option>
                    <option value="sante">Santé</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Niveau</label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="">Tous les niveaux</option>
                    <option value="1">Niveau 1</option>
                    <option value="2">Niveau 2</option>
                    <option value="3">Niveau 3</option>
                    <option value="4">Niveau 4</option>
                    <option value="5">Niveau 5</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Transversalité</label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="">Toutes</option>
                    <option value="high">Haute</option>
                    <option value="medium">Moyenne</option>
                    <option value="low">Basse</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="">Tous les types</option>
                    <option value="technique">Technique</option>
                    <option value="soft-skills">Soft Skills</option>
                    <option value="methodologique">Méthodologique</option>
                  </select>
                </div>
              </div>

              <button className="mt-4 w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all">
                Appliquer les filtres
              </button>
            </div>
          </div>
        );

      case 'visualisation':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-600" />
                Compétences communes
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="text-3xl font-bold text-purple-600">156</div>
                  <div className="text-sm text-gray-600 mt-1">Compétences partagées</div>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-3xl font-bold text-blue-600">89</div>
                  <div className="text-sm text-gray-600 mt-1">Synonymes détectés</div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-3xl font-bold text-green-600">234</div>
                  <div className="text-sm text-gray-600 mt-1">Correspondances validées</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'export':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileDown className="w-5 h-5 text-green-600" />
                Export des correspondances
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Exportez les correspondances détectées dans différents formats
              </p>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleExport('csv')}
                  className="flex items-center justify-center gap-2 px-6 py-4 bg-white border-2 border-green-300 text-green-700 rounded-lg font-medium hover:bg-green-50 transition-colors"
                >
                  <FileDown className="w-5 h-5" />
                  Export CSV
                </button>

                <button
                  onClick={() => handleExport('json')}
                  className="flex items-center justify-center gap-2 px-6 py-4 bg-white border-2 border-blue-300 text-blue-700 rounded-lg font-medium hover:bg-blue-50 transition-colors"
                >
                  <FileDown className="w-5 h-5" />
                  Export JSON
                </button>

                <button
                  onClick={() => handleExport('api')}
                  className="flex items-center justify-center gap-2 px-6 py-4 bg-white border-2 border-purple-300 text-purple-700 rounded-lg font-medium hover:bg-purple-50 transition-colors"
                >
                  <FileDown className="w-5 h-5" />
                  API REST
                </button>

                <button
                  onClick={() => handleExport('excel')}
                  className="flex items-center justify-center gap-2 px-6 py-4 bg-white border-2 border-indigo-300 text-indigo-700 rounded-lg font-medium hover:bg-indigo-50 transition-colors"
                >
                  <FileDown className="w-5 h-5" />
                  Export Excel
                </button>
              </div>
            </div>
          </div>
        );

      case 'ia-assistant':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Bot className="w-5 h-5 text-indigo-600" />
                Assistant IA
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                L'IA suggère des correspondances et aide à la validation humaine
              </p>

              {data?.suggestions && (
                <div className="space-y-3">
                  {data.suggestions.map((suggestion, index) => (
                    <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-medium text-gray-900">{suggestion.competence}</div>
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                          {(suggestion.confidence * 100).toFixed(0)}% confiance
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {suggestion.equivalents.map((eq, i) => (
                          <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                            {eq}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white shadow-lg">
              <GitBranch className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">Navette</h1>
              <p className="text-gray-600">Fusion & Matching - Intelligence relationnelle</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 mb-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
